import api from '../apis';
import environment from '../config/environment';
import logger from './logger';
import { CancelToken } from 'axios';
import { Loader } from '@googlemaps/js-api-loader';
import {
  GeocodeLocationResponse,
  GetUserLocationFromGeocodeResponseType,
  GoogleAddressComponent,
  GooglePlacesResponse,
  ReverseGeocodeResponse,
  StreetAddressComponents,
  UserLocation,
} from '../types/location';

let googlePlaces: google.maps.places.AutocompleteService | undefined;
let googleGeocoder: google.maps.Geocoder | undefined;

const loader = new Loader({
  apiKey: environment.GOOGLE_MAP_API_KEY,
});

export async function initGooglePlaces(): Promise<void> {
  loader
    .importLibrary('places')
    .then((places) => {
      googlePlaces = new places.AutocompleteService();
    })
    .catch((error) => {
      logger('error', error);
    });
}

export async function initGoogleGeocoder(): Promise<void> {
  loader
    .importLibrary('geocoding')
    .then(() => {
      googleGeocoder = new google.maps.Geocoder();
    })
    .catch((error) => {
      logger('error', 'Failed to initialize geocoder:', error);
    });
}

export async function getUserLocationFromAddress(
  placeId: string,
  address: string,
  cancelToken?: CancelToken,
): Promise<UserLocation | null> {
  try {
    const url = `${environment.GEOCODE_URL}/json?place_id=${placeId}&key=${environment.GOOGLE_MAP_API_KEY}`;
    const response = await api.get<GeocodeLocationResponse>(url, { cancelToken });
    if (response.data.status === 'OK') {
      const addressComponents = response.data.results[0].address_components;
      const cityComponent = addressComponents.find((e) => e.types.includes('locality'));
      const stateComponent = addressComponents.find((e) =>
        e.types.includes('administrative_area_level_1'),
      );
      const zipComponent = addressComponents.find((e) => e.types.includes('postal_code'));
      const userLocation: UserLocation = {
        address: address,
        street: getStreetAddressFromAddressComponents(addressComponents),
        place_id: placeId,
        latitude: response.data.results[0].geometry.location.lat,
        longitude: response.data.results[0].geometry.location.lng,
        city: cityComponent?.long_name ?? '',
        state: stateComponent?.long_name ?? '',
        zip: zipComponent?.long_name ?? '',
      };
      return userLocation;
    }
    logger('error', 'Invalid response while fetching geocode from location', response);
    return null;
  } catch (error) {
    logger('error', 'Error fetching geocode:', error);
    return null;
  }
}

export async function getUserLocationFromGeocode(
  lat: number,
  long: number,
): Promise<GetUserLocationFromGeocodeResponseType | null> {
  try {
    const url = `${environment.GEOCODE_URL}/json?latlng=${lat},${long}&key=${environment.GOOGLE_MAP_API_KEY}`;
    const response = await api.get<ReverseGeocodeResponse>(url);
    let countryComponent: GoogleAddressComponent | undefined | null = null;
    let cityComponent: GoogleAddressComponent | undefined | null = null;
    let stateComponent: GoogleAddressComponent | undefined | null = null;
    const streetAddressComponents: GoogleAddressComponent[] = [];
    let streetAddress: string | null = null;
    let zipComponent: GoogleAddressComponent | undefined | null = null;

    if (response.data.status === 'OK') {
      const postalCodeComponent = response.data.results.find((e) =>
        e.types.includes('postal_code'),
      );
      const streetAddressComponent = response.data.results[0];
      response.data.results.forEach((i) => {
        if (!countryComponent) {
          countryComponent = i.address_components.find((j) => j.types.includes('country'));
        }
        if (!cityComponent) {
          cityComponent = i.address_components.find((j) => j.types.includes('locality'));
        }
        if (!stateComponent) {
          stateComponent = i.address_components.find((j) =>
            j.types.includes('administrative_area_level_1'),
          );
        }
        if (!zipComponent) {
          zipComponent = i.address_components.find((j) => j.types.includes('postal_code'));
        }
      });

      const streetTypes = [
        'street_number',
        'route',
        'sublocality',
        'sublocality_level_3',
        'sublocality_level_2',
        'sublocality_level_1',
        'administrative_area_level_3',
      ];
      streetAddressComponent.address_components.forEach((e) => {
        if (streetTypes.some((type) => e.types.includes(type))) {
          streetAddressComponents.push(e);
        }
      });
      streetAddress = streetAddressComponents.map((obj) => obj.long_name).join(', ');
      let zip = '';
      if (zipComponent) zip = (zipComponent as GoogleAddressComponent).long_name;

      return {
        postalCodeComponent: postalCodeComponent,
        countryComponent: countryComponent,
        cityComponent: cityComponent,
        stateComponent: stateComponent,
        streetAddress: streetAddress,
        zip: zip,
        place_id: streetAddressComponent?.place_id,
      };
    }
    logger('error', 'Invalid response while fetching location from geocode', response);
    return null;
  } catch (error) {
    logger('error', 'Error reverse geocoding:', error);
    return null;
  }
}

export async function getPlacesPrediction(
  value: string,
  types: string[] | null = null,
): Promise<GooglePlacesResponse[]> {
  const request: google.maps.places.AutocompletionRequest = {
    input: value as string,
    types: types ?? ['locality', 'administrative_area_level_3', 'postal_code'],
    componentRestrictions: { country: 'us' },
  };
  const getNameWithoutCountry = (name: string) => {
    const strings = name.split(',');
    strings.pop();
    return strings.join(',');
  };
  return new Promise((resolve) => {
    googlePlaces?.getPlacePredictions(
      request,
      (res: google.maps.places.AutocompletePrediction[] | null) => {
        const list =
          res?.map((e) => {
            return {
              description: getNameWithoutCountry(e.description),
              place_id: e.place_id,
            };
          }) ?? [];
        const filteredList = list.filter((e) => e.description !== '');
        resolve(filteredList);
      },
    );
  });
}

/**
 * Extracts the street address from Google address components.
 *
 * @param {GoogleAddressComponent[]} addressComponents - Array of address components from Google API.
 * @returns {string} - The formatted street address.
 */
export const getStreetAddressFromAddressComponents = (
  addressComponents: GoogleAddressComponent[],
) => {
  // Initialize an object to hold the street address components.
  const streetAddressComponents: StreetAddressComponents = {
    street_number: null,
    route: null,
    subpremise: null,
  };

  // Iterate over the address components and assign relevant values to the object.
  addressComponents.forEach((component) => {
    if (component.types.includes('street_number')) {
      streetAddressComponents.street_number = component.long_name;
    } else if (component.types.includes('route')) {
      streetAddressComponents.route = component.long_name;
    } else if (component.types.includes('subpremise')) {
      streetAddressComponents.subpremise = `#${component.long_name}`;
    }
  });

  // Join the values to form the complete street address and remove any extra whitespace.
  const streetAddress = Object.values(streetAddressComponents).filter(Boolean).join(' ');

  return streetAddress.trim();
};

export async function getDetailedAddressFromPartial(
  partialAddress: { street?: string; city?: string; state?: string } | string,
): Promise<google.maps.GeocoderResult | null> {
  if (!googleGeocoder) {
    logger('error', 'Geocoder not initialized. Call initGoogleGeocoder() first.');
    return null;
  }

  if (
    !partialAddress ||
    (typeof partialAddress === 'object' &&
      !partialAddress.street &&
      !partialAddress.city &&
      !partialAddress.state)
  ) {
    logger('error', 'Invalid partial address provided.');
    return null;
  }

  return new Promise((resolve) => {
    googleGeocoder!.geocode(
      {
        address:
          typeof partialAddress === 'string'
            ? partialAddress
            : [partialAddress.street, partialAddress.city, partialAddress.state]
                .filter(Boolean)
                .join(', '),
        componentRestrictions: { country: 'US' },
      },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        const getUserLocation = () => {
          if (status !== 'OK' || !results || results.length === 0) {
            logger('error', 'No geocoding result found for the given address.');
            return null;
          }

          let resultWithMatchingState: google.maps.GeocoderResult[] | undefined = undefined;
          let resultsWithMatchingCity: google.maps.GeocoderResult[] | undefined = undefined;
          let resultsWithMatchingStreet: google.maps.GeocoderResult[] | undefined = undefined;

          if (typeof partialAddress === 'object' && partialAddress.state) {
            resultWithMatchingState = results.filter((result) =>
              result.address_components.some(
                (component) =>
                  partialAddress.state &&
                  component.types.includes('administrative_area_level_1') &&
                  component.long_name.toLowerCase() === partialAddress.state.toLowerCase(),
              ),
            );
          }

          if (typeof partialAddress === 'object' && partialAddress.city) {
            resultsWithMatchingCity = (resultWithMatchingState ?? results).filter((result) =>
              result.address_components.some(
                (component) =>
                  partialAddress.city &&
                  component.types.includes('locality') &&
                  component.long_name.toLowerCase() === partialAddress.city.toLowerCase(),
              ),
            );
          }

          if (typeof partialAddress === 'object' && partialAddress.street) {
            resultsWithMatchingStreet = (
              resultsWithMatchingCity ??
              resultWithMatchingState ??
              results
            ).filter((result) =>
              result.address_components.some(
                (component) =>
                  partialAddress.street &&
                  component.types.includes('route') &&
                  partialAddress.street.toLowerCase().includes(component.long_name.toLowerCase()),
              ),
            );
          }

          // Use matchingStreet if available, otherwise use the first result
          const result =
            (resultsWithMatchingStreet && resultsWithMatchingStreet[0]) ||
            (resultsWithMatchingCity && resultsWithMatchingCity[0]) ||
            (resultWithMatchingState && resultWithMatchingState[0]) ||
            results[0];

          return result;
        };

        const userLocation = getUserLocation();
        resolve(userLocation);
      },
    );
  });
}

export const getCoordinatesForAddress = async (
  street: string | undefined,
  city: string,
  state: string,
): Promise<{
  latitude?: number;
  longitude?: number;
  resolvedSuccessfully?: boolean;
}> => {
  try {
    if (!googleGeocoder) {
      throw new Error(`Error: Geocoder not initialized. Call initGoogleGeocoder() first.`);
    }

    const result = await getDetailedAddressFromPartial({ street, city, state });

    const streetAddressParts = [
      result?.address_components?.find((e) => e.types.includes('route'))?.long_name,
      result?.address_components?.find((e) => e.types.includes('street_number'))?.long_name,
      result?.address_components?.find(
        (e) =>
          e.types.includes('establishment') ||
          e.types.includes('point_of_interest') ||
          e.types.includes('transit_station'),
      )?.long_name,
    ];

    const resolvedCity = result?.address_components?.find((e) => e.types.includes('locality'))
      ?.long_name;
    const resolvedState = result?.address_components?.find((e) =>
      e.types.includes('administrative_area_level_1'),
    )?.long_name;

    let isStreetValidated = !street;

    streetAddressParts.forEach((part) => {
      if (part && street && street.includes(part)) {
        isStreetValidated = true;
      }
    });

    if (result && isStreetValidated && resolvedCity === city && resolvedState === state) {
      return {
        latitude: result.geometry.location.lat(),
        longitude: result.geometry.location.lng(),
        resolvedSuccessfully: true,
      };
    }
    return { resolvedSuccessfully: false };
  } catch (error) {
    const addressParts = [street, city, state].filter(Boolean).join(', ');
    throw new Error(
      `Error: While resolving latitude and longitude for "${addressParts}": ${error}`,
    );
  }
};
